import { FooterButtons, NullableKeys, Options } from '@fl/cmsch-fe-library';
import { isNull } from 'lodash/fp';
import React, { FC, memo, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { ContactType } from 'api/gen/ContactType';
import { useOurTranslation } from 'app/translations';
import { validate } from './validate';
import { formHelpers, useForm } from '../../../../utils/forms';
import { CreateOrEditOrNull } from '../../types/create-or-edit-or-null';
import { userContactFormName, userContactInitialValues, UserContactFormValues } from '../../types/user-contact-form-values';
interface Props {
  /** User-profile identifier that owns the contact being created or edited. */
  userProfileId: number;
  /** Modal mode that decides whether the form creates a new contact or edits an existing one. */
  mode: CreateOrEditOrNull;
  /** Existing contact data used to prefill the form when editing. */
  data: UserContactFormValues | null;
  /** Available contact-type options shown in the contact-type select field. */
  contactOptions: Options<ContactType>;
  /** Persists the current form values. */
  onSubmit(): void;
  /** Closes the modal without saving changes. */
  onCancel(): void;
  /** Triggers synchronous validation after a field value changes. */
  onSyncValidate(): void;
}
const ContactsFormBase: FC<Props> = ({
  onSubmit,
  onCancel,
  mode,
  userProfileId,
  contactOptions,
  onSyncValidate,
  data
}) => {
  const dispatch = useDispatch();
  const {
    Form,
    Fields,
    submitting,
    valid,
    pristine,
    asyncValidating
  } = useForm({
    form: userContactFormName,
    initialValues: userContactInitialValues,
    destroyOnUnmount: true,
    onSubmit,
    validate
  });
  const {
    t,
    tCommon
  } = useOurTranslation('userDetail');
  const isEdit = !isNull(data);

  // TODO (ondrej.krpec, 24. 6. 2024, TASK: 15071) Fix synchronous validation error when editing data in the form
  useEffect(() => {
    if (isEdit) {
      const initFormData: NullableKeys<UserContactFormValues> = {
        value: data.value,
        contactType: data.contactType,
        userProfileId,
        userContactId: data.userContactId
      };
      dispatch(formHelpers.initialize(userContactFormName, initFormData));
    } else {
      const initFormData: NullableKeys<UserContactFormValues> = {
        value: null,
        contactType: null,
        userProfileId,
        userContactId: null
      };
      dispatch(formHelpers.initialize(userContactFormName, initFormData));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return <Form data-sentry-element="Form" data-sentry-component="ContactsFormBase" data-sentry-source-file="index.tsx">
            <Fields.StringSelect name="contactType" label={t('contactType')} options={contactOptions} disabled={submitting || mode === 'edit'} onFieldChange={onSyncValidate} isRequired data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
            <Fields.Input name="value" type="text" label={t('contactValue')} disabled={submitting} onFieldChange={onSyncValidate} isRequired data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
            <FooterButtons submitting={submitting} valid={valid} pristine={pristine} onCancel={onCancel} asyncValidating={asyncValidating} submitTitle={mode === 'edit' ? tCommon('save') : tCommon('add')} data-sentry-element="FooterButtons" data-sentry-source-file="index.tsx" />
        </Form>;
};

/**
 * Renders the user contact form used by the contact tab modal.
 *
 * @param props Form state, contact options, and modal callbacks for the contact edit dialog.
 * @returns The contact form with type and value fields.
 *
 * @example
 * <ContactsForm
 *     userProfileId={userProfileId}
 *     mode="create"
 *     data={data}
 *     contactOptions={contactOptions}
 *     onSubmit={handleSubmit}
 *     onCancel={handleCancel}
 *     onSyncValidate={handleSyncValidate}
 * />
 */
export const ContactsForm = memo(ContactsFormBase);